import React, { useMemo, useCallback, useContext, FC } from 'react';
import { ThemeContext } from 'styled-components';

import useTranslation from '../../../hooks/useTranslation';
import { ReactComponent as CheckIcon } from '../../../icons/check.svg';
import { ReactComponent as CloseIcon } from '../../../icons/close.svg';
import { ReactComponent as RefreshIcon } from '../../../icons/replay.svg';
import { StyledFile, StyledFileLink, StyledButton, StyledCloseIcon, StyledIcon, StyledProgressBar, StyledLoader } from './FileList.styles';

interface FileProps {
  file: InstanceType<typeof window.File>;
  error?: Error;
  progress?: number;
  index?: number;
  pixelRatio?: number;
  success?: {};
  onRetry?: (index: number) => void;
  onDelete?: (index: number) => void;
}

const FileListDefaultProps = {
  success: {},
};

const File: FC<FileProps> = ({
  file, success, error, progress, index, pixelRatio, onRetry, onDelete, ...props
}) => {
  const theme = useContext(ThemeContext);
  const { t } = useTranslation('Core.FileList');
  const pending = useMemo(() => typeof progress === 'number' && !success && !error, [progress, success, error]);
  const baseRatio = useMemo(() => Math.floor(pixelRatio), [pixelRatio]);
  const borderWidth = useMemo(() => (1 / pixelRatio) * baseRatio, [baseRatio, pixelRatio]);
  const Icon = useMemo(() => error ? theme.icons.error : CheckIcon, [error, theme.icons.error]);

  const objectUrl = useMemo(() => {
    try {
      return URL.createObjectURL(file);
    } catch (e) {
      return '#';
    }
  }, [file]);

  const handleRetry = useCallback(() => {
    onRetry(index);
  }, [index, onRetry]);

  const handleDelete = useCallback(() => {
    onDelete(index);
  }, [index, onDelete]);

  return (
    <StyledFile
      borderWidth={borderWidth}
      {...props}
    >
      <StyledFileLink
        href={objectUrl}
        data-testid="FileListFileLink"
        target="_blank"
        pending={pending}
        error={error}
      >
        {pending && <StyledLoader size={18} />}
        {!pending && <StyledIcon as={Icon} error={error} />}
        {file.name}
      </StyledFileLink>
      {error && (
        <StyledButton
          onClick={handleRetry}
          data-testid="FileListFileRetryButton"
          title={t('retryButton')}
        >
          <StyledCloseIcon as={RefreshIcon} />
        </StyledButton>
      )}
      {(success || error || pending) && (
        <StyledButton
          onClick={handleDelete}
          data-testid="FileListFileCloseButton"
          title={t('closeButton')}
        >
          <StyledCloseIcon as={CloseIcon} />
        </StyledButton>
      )}
      {pending && <StyledProgressBar progress={progress} />}
    </StyledFile>
  );
};

File.defaultProps = FileListDefaultProps;

export default File;
